/* eslint-disable no-param-reassign */
/* eslint-disable max-len */
import {
  Component,
  h,
  Element,
  Prop,
  Host,
  State,
  Listen,
} from '@stencil/core';
import { versionComponent } from '../../global/inject-dependencies';

/**
 * This component, and tables in general, allow web developers to arrange data into rows and columns.
 */
@Component({
  tag: 'ui-table',
  styleUrl: 'ui-table.scss',
  shadow: false,
})
export class UiTable {
  headerVisible = true;

  @Element() el: HTMLUiTableElement;

  @Prop() firstColumnAsHeader = false;

  @Prop() firstRowAsHeader = false;

  @Prop() light = false;

  @Prop() coloredHead = false;

  @Prop() firstColumnFixed = true;

  @Prop() backgroundAlternating = false;

  @Prop() columnLayout: string;

  @State() columnWidth: number;

  @State() minNeeded: number;

  @State() initialTableWidth: number;

  @State() width: number;

  /**
   * flag to ensure we're calling handleTableClasses() only once for any given document width.
   *
   * Calling handleTableClasses() multiple times for the same document width, ie. on gridReady
   * and componentDidLoad has a high chance of messing up the calucations.
   */
  @State() tableClassesSet = false;

  private setClasses = (
    table: HTMLTableElement,
    tableRows: NodeListOf<HTMLTableRowElement>,
    veryLargeTable: boolean,
    tableSmallerThenWrapper: boolean,
    fixFits: boolean,
    contentIsSmall: boolean
  ) => {
    if (fixFits) {
      table.classList.remove('fullWidth', 'auto');
      table.classList.add('fullWidth', 'fixed');
    } else if (
      (!fixFits && tableSmallerThenWrapper) ||
      (!tableSmallerThenWrapper && contentIsSmall)
    ) {
      table.classList.remove('fullWidth', 'fixed');
      table.classList.add('fullWidth', 'auto');
    } else if (!tableSmallerThenWrapper) {
      if (!veryLargeTable) {
        table.classList.remove('fullWidth', 'fixed');
        table.classList.add('fullWidth', 'auto');
      } else if (veryLargeTable) {
        table.classList.remove('fullWidth', 'fixed', 'auto');
        table.classList.add('fullWidth', 'auto');
        Array.from(tableRows).forEach((row) =>
          Array.from(row.querySelectorAll('td')).forEach((cell) => {
            if (
              cell.clientHeight / 3 >
                this.median(Array.from(tableRows).map((r) => r.clientHeight)) &&
              cell.innerHTML.length > 100
            ) {
              cell.style.minWidth = `${cell.clientWidth * 2}px`;
            }
            return cell;
          })
        );
      }
    } else {
      table.classList.remove('fullWidth', 'fixed', 'auto');
    }
  };

  private handleTableClasses = () => {
    if (this.tableClassesSet) return;

    const table = this.el.querySelector('table') as HTMLTableElement;
    const tableRow = this.el.querySelector('tbody tr') as HTMLTableRowElement;
    const tableRows = this.el.querySelectorAll(
      'tr'
    ) as NodeListOf<HTMLTableRowElement>;
    const cells = this.el.querySelectorAll(
      'td'
    ) as NodeListOf<HTMLTableCellElement>;

    if (!this.minNeeded)
      this.minNeeded = this.getMaxColumnLength(tableRow.children);
    if (!this.initialTableWidth) this.initialTableWidth = table.clientWidth;

    const fixFits =
      this.minNeeded * tableRow.querySelectorAll('td').length <=
      this.el.clientWidth;
    const tableSmallerThenWrapper =
      this.initialTableWidth <= this.el.clientWidth;
    const contentIsSmall = Array.from(cells).every(
      (cell) =>
        cell.innerHTML.replace(/<\/?[^>]+(>|$)/g, '').split(' ').length <= 2
    );
    const veryLargeTable = this.el.clientWidth * 3 < this.initialTableWidth;

    this.setClasses(
      table,
      tableRows,
      veryLargeTable,
      tableSmallerThenWrapper,
      fixFits,
      contentIsSmall
    );

    Array.from(tableRows).forEach((row) =>
      Array.from(row.querySelectorAll('td')).forEach((cell) => {
        const contentArray: string[] = cell?.innerHTML.split(' ');
        if (
          contentArray.length === 2 &&
          contentArray.some((c) => c.length === 1)
        )
          cell.style.whiteSpace = 'nowrap';
      })
    );
    this.tableClassesSet = true;
  };

  private addResizeObserver() {
    window.addEventListener('resize', () => {
      this.tableClassesSet = false;
      this.handleTableClasses();
    });
  }

  private getMaxColumnLength = (tableHeader) =>
    Math.max(
      ...Array.from(tableHeader).map(
        (td: HTMLTableCellElement) => td.clientWidth
      )
    );

  private median = (arr) => {
    const mid = Math.floor(arr.length / 2);
    const nums = [...arr].sort((a, b) => a - b);
    return arr.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
  };

  componentDidLoad() {
    this.addResizeObserver();
    this.handleTableClasses();
    this.headerVisible = this.el.querySelectorAll('thead').length > 0;
  }

  @Listen('gridReady', { target: 'window' })
  onGridReady() {
    if (this.el.clientWidth) {
      this.handleTableClasses();
    }
  }

  render() {
    return (
      <Host
        class={{
          'first-column-frozen': this.firstColumnFixed,
          'no-header': !this.headerVisible,
          'first-column-as-header': this.firstColumnAsHeader,
          'first-row-as-header': this.firstRowAsHeader,
          light: this.light,
          'equal-width': this.columnLayout === 'equal',
        }}
      >
        <div class="table-wrapper">
          <slot></slot>
        </div>
      </Host>
    );
  }
}

versionComponent(UiTable);
